import { useState } from 'react';
import { supabase } from '../supabase.js';
import { fmtDate } from './FormBits.jsx';
import { DeadlineNote } from './ApprovalBits.jsx';

// Session 16: Replace one not-yet-started post of an approved plan.
//
//   ReplaceControl   the Replace button + optional note (and the late warning)
//                    for a v_portal_plan row with `replaceable`
//   ReplaceState     one line for a row with an open replacement
//   ReplaceReview    the decision on a generated replacement (v_portal_replacements):
//                    agency at agency_review, flow 'review' (Session 19, agency_only):
//                                             old vs new; Approve / Request changes / Discard
//                    agency at agency_review: Send to client / Try again / Cancel
//                    agency at sent:          Cancel
//                    client at sent:          Approve / Something else / Keep the original
//   FailedReplace    Session 19: a failed replacement in plain words, with
//                    Dismiss (portal_replace_dismiss) and Try again (the Replace
//                    box again, the old note filled in)
//
// Every write goes through portal_replace_plan_post / portal_replace_review /
// portal_replace_dismiss.

const stop = (e) => e.stopPropagation();

export function ReplaceControl({ row, isAgency, agencyOnly, onDone, initialNote = '', startOpen = false, onClose }) {
  const [open, setOpen] = useState(startOpen);
  const [note, setNote] = useState(initialNote || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  if (!row.replaceable) return null;

  async function go() {
    setBusy(true); setErr(null);
    const { error } = await supabase.rpc('portal_replace_plan_post', {
      p_plan_id: row.plan_id, p_slug: row.slug, p_note: note.trim() || null });
    setBusy(false);
    if (error) { setErr(error.message); return; }
    setOpen(false); setNote('');
    onDone?.(isAgency && agencyOnly
      ? 'Replacing it now. The new post comes back here for you to approve before it takes this slot.'
      : isAgency ? 'Replacing it now. You will review the new topic before the client sees it.'
        : "Thanks. We're finding a new topic for this date and will send it to you to approve.");
  }

  if (!open) {
    return (
      <button type="button" className="link-btn" onClick={(e) => { stop(e); setOpen(true); }}>Replace</button>
    );
  }
  return (
    <div className="replace-box" onClick={stop} onKeyDown={stop} role="group" aria-label={`Replace ${row.title}`}>
      <label className="field" style={{ margin: 0 }}>
        <span className="field-label">What would you like instead? (optional)</span>
        <textarea className="review-textarea" rows={2} value={note} maxLength={2000}
                  placeholder="e.g. make it a case study about the Keller project" onChange={(e) => setNote(e.target.value)} />
      </label>
      {row.replace_late && (
        <p className="banner banner-waiting" style={{ margin: '8px 0 0' }}>
          This post is less than the usual questions lead time away. You can still replace it; the questions go out
          as soon as the new topic is {isAgency && agencyOnly ? 'ready' : 'approved'}.
        </p>
      )}
      {err && <p className="banner banner-err" style={{ margin: '8px 0 0' }}>{err}</p>}
      <div className="review-actions" style={{ marginTop: 8 }}>
        <button type="button" className="link-btn" disabled={busy} onClick={() => { setOpen(false); setErr(null); onClose?.(); }}>Cancel</button>
        <button type="button" className="btn btn-primary" disabled={busy} onClick={go}>{busy ? 'Sending…' : 'Replace this post'}</button>
      </div>
    </div>
  );
}

export function replaceStateText(status, isAgency) {
  if (!status) return null;
  if (['queued', 'running'].includes(status)) return isAgency ? 'Replacing… (generating a new post)' : "We're finding a new topic for this date.";
  if (status === 'agency_review') return isAgency ? 'Replacement ready for your review' : 'Your new topic is with Digital Lotus for a quick check.';
  if (status === 'sent') return isAgency ? 'Replacement sent to the client, waiting for approval' : 'A new topic is ready for you to approve.';
  if (status === 'approved') return 'Replacement approved, updating the plan…';
  return null;
}

export function ReplaceState({ row, isAgency }) {
  const text = replaceStateText(row.replace_status, isAgency);
  if (!text) return null;
  return <span className="sub replace-state">{text}</span>;
}

// Session 19: post type FLAGSHIP -> Flagship.
const titleCase = (s) => String(s || '').toLowerCase().replace(/(^|\s)([a-z])/g, (_m, a, b) => a + b.toUpperCase());

// Session 19: old vs new, one row per field (agency review of a 'review' replace).
function CompareRows({ old, rep }) {
  const rows = [
    ['Title', old?.title || rep.old_title, rep.new_title],
    ['Slug', old?.slug || rep.slug, rep.new_slug],
    ['Excerpt', old?.excerpt, rep.new_excerpt],
    ['Keyword', old?.primary_keyword, rep.new_keyword],
    ['Post type', old?.post_type && titleCase(old.post_type), rep.new_post_type && titleCase(rep.new_post_type)],
    ['Why now', old?.why_now, rep.new_why_now],
  ];
  const qs = (list) => ((list || []).length ? <ol>{list.map((q, i) => <li key={i}>{q}</li>)}</ol> : null);
  return (
    <div className="replace-compare" role="table" aria-label="Current post and replacement">
      <div className="replace-compare-row replace-compare-head" role="row">
        <span role="columnheader" /><span role="columnheader">Now</span><span role="columnheader">Replacement</span>
      </div>
      {rows.map(([label, a, b]) => (
        <div key={label} className="replace-compare-row" role="row">
          <span className="replace-compare-label" role="rowheader">{label}</span>
          <span role="cell" className="replace-compare-old" data-label="Now">{a || '—'}</span>
          <span role="cell" className={a !== b ? 'replace-compare-new changed' : 'replace-compare-new'} data-label="Replacement">{b || '—'}</span>
        </div>
      ))}
      <div className="replace-compare-row" role="row">
        <span className="replace-compare-label" role="rowheader">Questions</span>
        <span role="cell" className="replace-compare-old" data-label="Now">{qs(old?.questions) || '—'}</span>
        <span role="cell" className="replace-compare-new" data-label="Replacement">{qs(rep.new_questions) || '—'}</span>
      </div>
    </div>
  );
}

export function ReplaceReview({ rep, isAgency, onDone, old }) {
  const [note, setNote] = useState('');
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  if (!rep) return null;
  const agencyGate = isAgency && rep.status === 'agency_review';
  const reviewFlow = agencyGate && rep.flow === 'review';   // Session 19: the agency approves; never sent to the client
  const clientTurn = !isAgency && rep.status === 'sent';
  if (!agencyGate && !clientTurn && !(isAgency && rep.status === 'sent')) return null;

  async function act(action) {
    if (action === 'regenerate' && !note.trim()) { setErr('Tell us what you would like instead.'); return; }
    setBusy(true); setErr(null);
    const { data, error } = await supabase.rpc('portal_replace_review', {
      p_job_id: rep.job_id, p_action: action, p_note: action === 'regenerate' ? note.trim() : null });
    setBusy(false);
    if (error) { setErr(error.message); return; }
    if (typeof data === 'string' && data.startsWith('refused')) { setErr(`Not applied: ${data.replace(/^refused:\s*/, '')}`); return; }
    setNote(''); setAsking(false);
    onDone?.({
      send: `Sent to the client.`,
      regenerate: 'Asked for another topic. It comes back here when it is ready.',
      cancel: 'Cancelled. The original post stays.',
      approve: 'Approved. Your plan now shows the new post.',
      keep: 'Kept the original post.',
      ...(reviewFlow ? { regenerate: 'Asked for changes. The new version comes back here when it is ready.',
                         cancel: 'Discarded. The original post stays.' } : {}),
    }[action]);
  }

  return (
    <div className="replace-review" onClick={stop} onKeyDown={stop}>
      <p className="replace-titles">
        <span className="hint" style={{ margin: 0 }}>{fmtDate(rep.publish_date)}</span><br />
        <s>{rep.old_title}</s> → <strong>{rep.new_title || '…'}</strong>
      </p>
      {rep.new_client_summary && !reviewFlow && <p className="plan-card-why">{rep.new_client_summary}</p>}
      {reviewFlow && <CompareRows old={old} rep={rep} />}
      {reviewFlow && rep.note && <p className="hint" style={{ margin: '8px 0 0' }}>Your note: {rep.note}</p>}
      {isAgency && !reviewFlow && (
        <ul className="replace-meta">
          {rep.new_keyword && <li><strong>Keyword / topic</strong> {rep.new_keyword}</li>}
          {rep.new_why_now && <li><strong>Why now</strong> {rep.new_why_now}</li>}
          {rep.new_excerpt && <li><strong>Excerpt</strong> {rep.new_excerpt}</li>}
          {(rep.new_questions || []).length > 0 && (
            <li><strong>Questions</strong><ol>{rep.new_questions.map((q, i) => <li key={i}>{q}</li>)}</ol></li>
          )}
          {rep.note && <li><strong>Note</strong> {rep.note} ({rep.requested_role === 'client' ? 'client' : 'you'})</li>}
          {rep.late && <li><strong>Late</strong> the questions go out as soon as it is approved</li>}
        </ul>
      )}
      {clientTurn && rep.auto_approves && <DeadlineNote at={rep.deadline_at} />}
      {asking && (
        <label className="field" style={{ marginTop: 8 }}>
          <span className="field-label">{reviewFlow ? 'What should change?' : 'What would you like instead?'}</span>
          <textarea className="review-textarea" rows={2} value={note} maxLength={2000} onChange={(e) => setNote(e.target.value)} />
        </label>
      )}
      {err && <p className="banner banner-err" style={{ margin: '8px 0 0' }}>{err}</p>}
      <div className="review-actions" style={{ marginTop: 8 }}>
        {reviewFlow && <>
          <button type="button" className="link-btn" disabled={busy} onClick={() => act('cancel')}>Discard</button>
          {asking
            ? <button type="button" className="btn" disabled={busy} onClick={() => act('regenerate')}>Send note</button>
            : <button type="button" className="btn" disabled={busy} onClick={() => setAsking(true)}>Request changes</button>}
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => act('approve')}>Approve</button>
        </>}
        {agencyGate && !reviewFlow && <>
          <button type="button" className="link-btn" disabled={busy} onClick={() => act('cancel')}>Cancel replacement</button>
          {asking
            ? <button type="button" className="btn" disabled={busy} onClick={() => act('regenerate')}>Send note</button>
            : <button type="button" className="btn" disabled={busy} onClick={() => setAsking(true)}>Try again</button>}
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => act('send')}>Send to client</button>
        </>}
        {isAgency && rep.status === 'sent' && (
          <button type="button" className="link-btn" disabled={busy} onClick={() => act('cancel')}>Cancel replacement</button>
        )}
        {clientTurn && <>
          <button type="button" className="link-btn" disabled={busy} onClick={() => act('keep')}>Keep the original</button>
          {asking
            ? <button type="button" className="btn" disabled={busy} onClick={() => act('regenerate')}>Send note</button>
            : <button type="button" className="btn" disabled={busy} onClick={() => setAsking(true)}>Something else</button>}
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => act('approve')}>Approve new topic</button>
        </>}
      </div>
    </div>
  );
}

// Session 19: the error in plain words, plus what to do about the common one.
export function failureText(error) {
  const e = String(error || '').trim();
  const out = { text: e ? `The replacement couldn't be made: ${e.replace(/^refused:\s*/, '')}` : "The replacement couldn't be made.", hint: null };
  if (/repeats a planned post/i.test(e)) {
    out.hint = 'Another post in the plan already uses this title. Pick a different title, or Replace again with a note.';
  } else if (/did not finish within/i.test(e)) {
    out.hint = 'It took too long and was stopped. Try again; it usually works the second time.';
  }
  return out;
}

// Session 19: a failed replacement (rep = v_portal_replacements row, newest
// for its post). `row` is the v_portal_plan row when the page has it; without
// it Try again builds one from rep (the RPC re-checks everything).
export function FailedReplace({ rep, row, agencyOnly, onDone, compact = false }) {
  const [retry, setRetry] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  if (!rep || rep.status !== 'failed') return null;
  const { text, hint } = failureText(rep.error);
  const target = row || { replaceable: true, plan_id: rep.plan_id, slug: rep.slug, title: rep.old_title, replace_late: rep.late };

  async function dismiss(e) {
    stop(e); e.preventDefault();
    setBusy(true); setErr(null);
    const { error } = await supabase.rpc('portal_replace_dismiss', { p_job_id: rep.job_id });
    setBusy(false);
    if (error) { setErr(error.message); return; }
    onDone?.('Dismissed. The original post stays.', 'dismissed');
  }

  return (
    <div className={`replace-failed ${compact ? 'compact' : ''}`} onClick={stop} onKeyDown={stop}>
      {!compact && <p className="replace-failed-text">{text}</p>}
      {hint && <p className="replace-failed-hint">{hint}</p>}
      {err && <p className="banner banner-err" style={{ margin: '6px 0 0' }}>{err}</p>}
      {retry ? (
        <ReplaceControl row={target} isAgency agencyOnly={agencyOnly} initialNote={rep.note || ''} startOpen
                        onClose={() => setRetry(false)} onDone={(t) => { setRetry(false); onDone?.(t, 'retried'); }} />
      ) : (
        <div className="review-actions" style={{ marginTop: 6 }}>
          <button type="button" className="link-btn" disabled={busy} onClick={dismiss}>Dismiss</button>
          <button type="button" className="btn" disabled={busy} onClick={(e) => { stop(e); e.preventDefault(); setRetry(true); }}>Try again</button>
        </div>
      )}
    </div>
  );
}
